import { useCallback, useEffect, useRef, useState } from "react";
import { InputField } from "components";
import SC from "./style";
import theme from "theme";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import QuillToolbar, { modules, formats } from "./QuillToolbar";
import { byteToMB, showAlert } from "utils/helper";
import TooltipText from "components/TooltipText";
import { MAX_FILE_SIZE } from "utils/config";

const MessageContent = (props) => {
  const { setData, data, errorMessage } = props;
  const [files, setFiles] = useState(data?.content?.files || []);
  const [filePreviews, setFilePreviews] = useState([]);
  const fileInputRef = useRef(null);
  const [value, setValue] = useState(null);

  const MAX_TOTAL_SIZE = MAX_FILE_SIZE; // 20MB in bytes

  const fileSizeErrorMessage = `Total file size exceeds the ${byteToMB(MAX_FILE_SIZE)} MB limit.`;

  useEffect(() => {
    if (data?.content?.files && data?.content?.files.length > 0) {
      // Generate previews, skipping .txt files
      const previews = data.content?.files.map((file) => {
        if (file.type === "text/plain") {
          // Handle .txt files (return null or custom value)
          return null;
        }
        return file.fileUrl || URL.createObjectURL(file);
      });

      setFilePreviews(previews);

      // Cleanup function to revoke object URLs to avoid memory leaks
      return () => {
        previews.forEach((preview) => {
          if (preview?.startsWith("blob:")) {
            URL.revokeObjectURL(preview);
          }
        });
      };
    } else {
      // Clear previews if no files exist
      setFilePreviews([]);
    }
  }, [data?.content?.files]);

  // Handle changes for subject, message, and file
  const handleChange = (property, value) => {
    if (property === "subject") {
      if (value.length >= 255) {
        showAlert({
          title: "Warning",
          text: "Limit exceeded",
          icon: "warning",
        });
      }
    }
    setData((prevData) => ({
      ...prevData,
      content: {
        ...prevData.content,
        [property]: value,
      },
    }));
  };

  const handleChangeTextEditor = useCallback(
    (newValue) => {
      setValue(newValue);
      setData((prevData) => ({
        ...prevData,
        content: {
          ...prevData.content,
          message: newValue,
        },
      }));
    },
    [setData],
  );

  const handleFileChange = (e) => {
    const fileInput = e.target;
    const selectedFiles = Array.from(fileInput.files);

    // Check total file size limit
    const newTotalSize =
      files.reduce((sum, file) => sum + file.size, 0) +
      selectedFiles.reduce((sum, file) => sum + file.size, 0);

    if (newTotalSize > MAX_TOTAL_SIZE) {
      showAlert({
        title: "Error",
        text: fileSizeErrorMessage,
        icon: "error",
      });
      fileInput.value = ""; // Clear input
      return;
    }

    // Filter valid files based on individual size
    const validFiles = selectedFiles.filter(
      (file) => file.size <= MAX_TOTAL_SIZE,
    );

    // Notify about invalid files
    if (validFiles.length < selectedFiles.length) {
      showAlert({
        title: "Error",
        text: fileSizeErrorMessage,
        icon: "error",
      });
    }

    // Create previews for valid files
    const newPreviews = validFiles.map((file) => URL.createObjectURL(file));

    // Update state with new files and previews
    setFilePreviews((prevPreviews) => [...prevPreviews, ...newPreviews]);

    setFiles((prevFiles) => [...prevFiles, ...validFiles]);
    setData((prevData) => ({
      ...prevData,
      content: {
        ...prevData.content,
        files: [...(prevData.content.files || []), ...validFiles],
      },
    }));

    // Clear input for re-selection
    setTimeout(() => {
      fileInput.value = "";
    }, 0);
  };

  const handleRemoveFile = (index) => {
    // Remove file and preview
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    setFilePreviews((prevPreviews) =>
      prevPreviews.filter((_, i) => i !== index),
    );

    // Update files in data
    setData((prevData) => ({
      ...prevData,
      content: {
        ...prevData.content,
        files: prevData?.files?.filter((_, i) => i !== index),
      },
    }));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);

    // Filter valid files and limit size
    const validFiles = droppedFiles.filter(
      (file) => file.size <= MAX_TOTAL_SIZE,
    );

    if (validFiles.length < droppedFiles.length) {
      showAlert({
        title: "Error",
        text: fileSizeErrorMessage,
        icon: "error",
      });
    }

    const newPreviews = validFiles.map((file) => URL.createObjectURL(file));

    // Update state with new files and previews
    setFiles((prevFiles) => [...prevFiles, ...validFiles]);
    setFilePreviews((prevPreviews) => [...prevPreviews, ...newPreviews]);

    // Set files to data
    setData((prevData) => ({
      ...prevData,
      content: {
        ...prevData.content,
        files: [...(prevData.files || []), ...validFiles],
      },
    }));
  };

  const handleFileDialogClick = () => {
    fileInputRef.current.click();
  };

  return (
    <>
      {/* Subject */}
      <SC.LabelContainer>
        <SC.Label>
          Subject <SC.Astrik>*</SC.Astrik>
        </SC.Label>
        <SC.Children>
          <SC.Wrapper>
            <form
              autoComplete="off"
              style={{ width: "100%", height: "35px" }}
              onSubmit={(event) => {
                event.preventDefault();
              }}
            >
              <InputField
                name="subject"
                value={data?.content?.subject}
                onChange={handleChange}
                required
                style={{ width: "100%", height: "35px" }}
                maxLength={255}
              />
            </form>
          </SC.Wrapper>
          {errorMessage && (
            <span style={{ color: theme.colors.red, fontSize: "12px" }}>
              {errorMessage}
            </span>
          )}
        </SC.Children>
      </SC.LabelContainer>

      {/* Message */}
      <SC.LabelContainer style={{ alignItems: "flex-start" }}>
        <SC.Label>Message</SC.Label>
        <SC.Children>
          <SC.Wrapper style={{ flexDirection: "column" }}>
            <div>
              <QuillToolbar />
              <ReactQuill
                theme="snow"
                value={data?.content?.message}
                onChange={handleChangeTextEditor}
                placeholder="Write something awesome..."
                modules={modules}
                formats={formats}
                style={{ height: "100%" }}
              />
            </div>
          </SC.Wrapper>
        </SC.Children>
      </SC.LabelContainer>

      {/* Attachment */}

      <SC.LabelContainer style={{ alignItems: "flex-start" }}>
        <SC.Label>Attachment</SC.Label>
        <SC.Children>
          <SC.Wrapper style={{ flexDirection: "column" }}>
            <SC.DropzoneWrapper
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={handleFileDialogClick}
            >
              <SC.UploadFileContiner>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <SC.Span>
                    <SC.UploadIcon />
                  </SC.Span>
                  <div>
                    <span style={{ fontSize: "14px" }}>Drop your file or</span>
                    <span
                      style={{
                        color: theme.colors.blue,
                        fontSize: "14px",
                      }}
                    >
                      {" "}
                      browse
                    </span>
                  </div>
                  <span style={{ fontSize: "12px", opacity: "0.8" }}>
                    Max 25 MB file is allowed
                  </span>
                </div>
              </SC.UploadFileContiner>
            </SC.DropzoneWrapper>
            <input
              multiple
              type="file"
              name="file[]"
              style={{ display: "none" }}
              onChange={handleFileChange}
              ref={fileInputRef}
            />
            <span
              style={{
                fontSize: "12px",
                opacity: "0.8",
                padding: "10px 0px 10px 0px",
              }}
            >
              {/* Only support .jpg, .png, .pdf, and zip files */}
            </span>

            <SC.PreviewsContainer>
              {filePreviews.map((preview, index) => {
                const file = data?.content?.files[index];
                const fileSize =
                  file?.size > 1048576
                    ? `${(file.size / 1048576).toFixed(2)} MB`
                    : `${(file.size / 1024).toFixed(2)} KB`;

                return (
                  <SC.PreviewBox key={index}>
                    <SC.PreviewContainer>
                      <SC.Image src={preview} alt="file preview" />
                      <SC.FileName>
                        <TooltipText title={file?.name} maxLength={60} />
                        <SC.FileSize>{fileSize}</SC.FileSize>
                      </SC.FileName>
                    </SC.PreviewContainer>
                    <SC.CloseIcon onClick={() => handleRemoveFile(index)} />
                  </SC.PreviewBox>
                );
              })}
            </SC.PreviewsContainer>
          </SC.Wrapper>
        </SC.Children>
      </SC.LabelContainer>
    </>
  );
};

export default MessageContent;
